import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataOptional } from '../../contexts/DataContext';
import UpcomingAssessmentsList from './UpcomingAssessmentsList';
import InsightPanel from './InsightPanel';
import useAcademicInsights from './useAcademicInsights';
import './HealthDrawer.css';

/**
 * HealthDrawer — Status panel above Study Timeline.
 *
 * Layout:
 *   Header: "Status:" + pill + close
 *   Mid Grid:
 *     Left:  Snapshot card (Problems + Wins)
 *     Right: Top priorities (actionable rows)
 *   Footer: Recommendation callout with "Open" button
 */
export default function HealthDrawer({
  health,
  onClose,
  assessments = [],
  totalCount = 0,
  onAssignmentClick,
  onEventClick,
  hasCourses: hasCourseProp = true,
}) {
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  const [showAllProblems, setShowAllProblems] = useState(false);
  const [showAllWins, setShowAllWins] = useState(false);

  // Derive hasCourses from DataContext directly (more reliable than prop)
  const dataCtx = useDataOptional() || {};
  const courseKeys = Object.keys(dataCtx.state?.coursesById || {});
  const hasCourses = courseKeys.length > 0;
  console.log('[HealthDrawer] coursesById keys:', courseKeys, 'hasCourses:', hasCourses);

  /* ── Close on outside click ── */
  useEffect(() => {
    function handler(e) {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        // Let the AcademicHealthBadge toggle handle open/close itself
        if (e.target.closest('.ahb') || e.target.closest('.btn--primary')) return;
        onClose();
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  /* ── Close on Escape ── */
  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  /* ── Navigate to assignment / event detail ── */
  // eslint-disable-next-line no-unused-vars
  const handlePriorityClick = useCallback((item) => {
    if (item.type === 'assignment' && item.courseId) {
      navigate(`/course/${item.courseId}?tab=activities&assignment=${item.assignmentId}`);
    } else if (item.type === 'event' && item.courseId) {
      navigate(`/course/${item.courseId}?tab=activities&event=${item.eventId}`);
    }
    onClose();
  }, [navigate, onClose]);

  /* ── Assessment row click — delegate to parent callbacks ── */
  const handleAssessmentClick = useCallback((item) => {
    if (item.source === 'assignment' && onAssignmentClick) {
      onAssignmentClick(item.raw);
    } else if (item.source === 'event' && onEventClick) {
      onEventClick(item.raw);
    }
    onClose();
  }, [onAssignmentClick, onEventClick, onClose]);

  /* ── Academic insights ── */
  const { insights } = useAcademicInsights(health, assessments);

  const statusLabel =
    health.status === 'critical' ? 'Critical'
    : health.status === 'warning' ? 'Warning'
    : 'On Track';

  const problems = health.problems || [];
  const wins = health.wins || [];
  const visibleProblems = showAllProblems ? problems : problems.slice(0, 3);
  const visibleWins = showAllWins ? wins : wins.slice(0, 3);

  return (
    <>
      <div className="hd-scrim" onClick={onClose} />

      <aside ref={drawerRef} className={`hd-drawer${!hasCourses ? ' hd-drawer--empty' : ''}`} role="dialog" aria-label="Status details">
        {/* ── Header ── */}
        <div className="hd-header">
          <div className="hd-headerLeft">
            <h2 className="hd-title">{hasCourses ? 'Status:' : 'Academic Health'}</h2>
            {hasCourses ? (
              <span className={`hd-pill hd-pill--${health.status}`}>
                <span className="hd-pillDot" />
                {statusLabel}
              </span>
            ) : (
              <span className="hd-pill hd-pill--dormant">
                <span className="hd-pillDot" />
                Ready
              </span>
            )}
          </div>
          <button className="hd-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {!hasCourses ? (
          /* ── Zero-courses empty state ── */
          <div className="hd-empty-body">
            <div className="hd-empty-intro">
              <div className="hd-empty-icon-ring">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="hd-empty-headline">Insights will appear once your courses are added</h3>
              <p className="hd-empty-desc">
                Add courses, assignments, and key dates to unlock workload insights,
                schedule awareness, and academic health recommendations.
              </p>
            </div>

            <div className="hd-empty-grid">
              {/* Left — feature preview */}
              <div className="hd-empty-preview-card">
                <h4 className="hd-empty-preview-title">What Academic Health will track</h4>
                <ul className="hd-empty-preview-list">
                  <li>Workload across upcoming days</li>
                  <li>Overdue and completed assignments</li>
                  <li>Class and deadline pressure</li>
                  <li>Study pacing across your week</li>
                </ul>
              </div>

              {/* Right — insight preview */}
              <div className="hd-empty-preview-card">
                <span className="hd-empty-insight-label">Academic Insight</span>
                <h4 className="hd-empty-preview-title">Your first insight will show here</h4>
                <p className="hd-empty-preview-body">
                  Once your dashboard has course activity, we'll surface patterns and recommendations here.
                </p>
                <ul className="hd-empty-preview-list hd-empty-preview-list--muted">
                  <li>Upcoming workload spikes</li>
                  <li>Gaps in assignment completion</li>
                  <li>Best weeks to get ahead</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          /* ── Normal populated state ── */
          <>
            <div className="hd-midGrid">
          {/* Left: Snapshot card */}
          <div className="hd-snapshotCard">
            <h3 className="hd-sectionLabel">
              Health Score:{' '}
              <span className={`hd-healthScoreInline hd-healthScoreInline--${health.status}`}>
                {health.healthScore ?? '—'}
              </span>
              <span className="hd-healthScoreInlineMax">/ 100</span>
            </h3>

            {/* Problems */}
            {problems.length > 0 && (
              <div className="hd-snapSection">
                <div className="hd-snapHeader hd-snapHeader--problems">
                  <span className="hd-snapIcon" aria-hidden="true">▲</span>
                  <span className="hd-snapHeading">Problems</span>
                </div>
                <ul className="hd-snapList">
                  {visibleProblems.map((p) => (
                    <li key={p.key} className="hd-snapItem hd-snapItem--problem">
                      <span className="hd-snapDot hd-snapDot--red" aria-hidden="true" />
                      {p.text}
                    </li>
                  ))}
                </ul>
                {problems.length > 3 && !showAllProblems && (
                  <button className="hd-snapMore" onClick={() => setShowAllProblems(true)}>
                    +{problems.length - 3} more
                  </button>
                )}
              </div>
            )}

            {/* Wins */}
            <div className="hd-snapSection">
              <div className="hd-snapHeader hd-snapHeader--wins">
                <span className="hd-snapIcon" aria-hidden="true">✓</span>
                <span className="hd-snapHeading">Wins</span>
              </div>
              <ul className="hd-snapList">
                {visibleWins.map((w) => (
                  <li key={w.key} className="hd-snapItem hd-snapItem--win">
                    <span className="hd-snapDot hd-snapDot--green" aria-hidden="true" />
                    {w.text}
                  </li>
                ))}
              </ul>
              {wins.length > 3 && !showAllWins && (
                <button className="hd-snapMore" onClick={() => setShowAllWins(true)}>
                  +{wins.length - 3} more
                </button>
              )}
            </div>
          </div>

          {/* Right: Academic Insight panel */}
          <InsightPanel insights={insights} />
        </div>

        {/* ── Upcoming Assessments ── */}
        <UpcomingAssessmentsList
          assessments={assessments}
          totalCount={totalCount}
          onItemClick={handleAssessmentClick}
        />
          </>
        )}
      </aside>
    </>
  );
}
